import { normalizeStateCode } from '@stfors/shared-types';
import type { LookupOption, PersonAddress, PersonAddressInput } from '@stfors/shared-types';
import { AddressFields } from './AddressFields';
import { composeStreet, parseStreet, type AddressValue } from '../utils/address';
import './ContactListInput.css';

// A person's editable address row = a structured AddressValue plus a `label`
// (the admin-managed address_type). The street is split for entry but stored
// as a single `line1`; compose/parse live in utils/address.
export interface AddressEntry extends AddressValue {
  label: string;
}

export const emptyAddress = (): AddressEntry => ({
  label: '', houseNumber: '', streetDirection: '', streetName: '', streetType: '',
  streetPostDirection: '', line2: '',
  city: '', state: '', postal_code: '', country: '',
});

/** Edit rows → API payload: drop empty rows; compose the street into line1. */
export function addressesToPayload(rows: AddressEntry[]): PersonAddressInput[] {
  return rows
    .filter((r) => Object.values(r).some((v) => v.trim().length > 0))
    .map((r) => ({
      label: r.label.trim() || null,
      line1: composeStreet(r) || null,
      line2: r.line2.trim() || null,
      city: r.city.trim() || null,
      state: r.state.trim() || null,
      postal_code: r.postal_code.trim() || null,
      country: r.country.trim() || null,
    }));
}

/** Loaded person addresses → editable rows (state coerced to a 2-letter code). */
export function addressesFromPerson(addrs: PersonAddress[]): AddressEntry[] {
  return addrs.map((a) => ({
    label: a.label ?? '',
    ...parseStreet(a.line1),
    line2: a.line2 ?? '',
    city: a.city ?? '',
    state: normalizeStateCode(a.state) ?? '',
    postal_code: a.postal_code ?? '',
    country: a.country ?? '',
  }));
}

interface AddressListInputProps {
  value: AddressEntry[];
  onChange: (next: AddressEntry[]) => void;
  /** Admin-managed address-type labels (lookup_options category 'address_type'). */
  typeOptions: LookupOption[];
  disabled?: boolean;
}

export function AddressListInput({ value, onChange, typeOptions, disabled = false }: AddressListInputProps): JSX.Element {
  const setLabel = (idx: number, label: string) =>
    onChange(value.map((row, i) => (i === idx ? { ...row, label } : row)));
  const setAddr = (idx: number, addr: AddressValue) =>
    onChange(value.map((row, i) => (i === idx ? { ...row, ...addr } : row)));
  const removeRow = (idx: number) => onChange(value.filter((_, i) => i !== idx));
  const addRow = () => onChange([...value, emptyAddress()]);
  // Primary = the first row (the API flags addresses[0].is_primary on save).
  const makePrimary = (idx: number) => {
    if (idx === 0) return;
    const next = [...value];
    const [row] = next.splice(idx, 1);
    next.unshift(row!);
    onChange(next);
  };

  return (
    <div className="contact-list">
      {value.length === 0 && <p className="contact-list-empty">No addresses yet.</p>}
      {value.map((row, idx) => {
        const { label, ...addr } = row;
        return (
          <div key={idx} className="addr-entry">
            <div className="addr-row">
              <select
                className={`form-input contact-list-label${label === '' ? ' is-placeholder' : ''}`}
                value={label}
                onChange={(e) => setLabel(idx, e.target.value)}
                disabled={disabled}
                aria-label={`Address ${idx + 1} type`}
              >
                <option value="">— Type —</option>
                {label && !typeOptions.some((o) => o.value === label) && (
                  <option value={label}>{label} (legacy)</option>
                )}
                {typeOptions.map((o) => <option key={o.id} value={o.value}>{o.value}</option>)}
              </select>
              <span className="addr-street-label">Street Address</span>
              {value.length > 1 && (idx === 0 ? (
                <span className="contact-list-primary" title="Primary address">Primary</span>
              ) : (
                <button
                  type="button"
                  className="contact-list-make-primary"
                  onClick={() => makePrimary(idx)}
                  disabled={disabled}
                  title="Make this the primary address"
                >
                  Make primary
                </button>
              ))}
              <button
                type="button"
                className="contact-list-remove"
                onClick={() => removeRow(idx)}
                disabled={disabled}
                aria-label={`Remove address ${idx + 1}`}
              >
                ×
              </button>
            </div>
            <AddressFields value={addr} onChange={(a) => setAddr(idx, a)} disabled={disabled} idPrefix={`Address ${idx + 1}`} />
          </div>
        );
      })}
      <button type="button" className="contact-list-add" onClick={addRow} disabled={disabled}>
        + Add address
      </button>
    </div>
  );
}
